import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { isApiError, login, register } from '../services/api';
import { User, BookOpen, ArrowRight, Users, Trophy, Clock } from 'lucide-react';

interface LoginPageProps {
  onLogin?: (username: string) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pseudonym, setPseudonym] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  // D — message posé par l'interception globale des 401 (session expirée).
  const { sessionExpired, clearSessionExpired } = useUser();
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (sessionExpired) {
      setNotice(sessionExpired);
      clearSessionExpired();
    }
  }, [sessionExpired, clearSessionExpired]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim() && password && (!isRegisterMode || pseudonym.trim())) {
      setIsLoading(true);
      setError('');

      try {
        const user = isRegisterMode
          ? await register({ email: email.trim(), password, pseudonym: pseudonym.trim() })
          : await login({ email: email.trim(), password });

        if (onLogin) {
          onLogin(user.pseudonym);
        } else {
          const target = user.role === 'admin' ? '/admin' : '/';
          // Force a full reload so the cookie is applied and useUser() refetches
          window.location.href = target;
        }
      } catch (error) {
        if (isApiError(error) && !error.isNetworkError) {
          // Erreur de validation détaillée (liste) sinon message du serveur
          setError(
            error.details.length > 0
              ? error.details.join(', ')
              : error.message || (isRegisterMode ? 'Erreur lors de la création du compte' : 'Erreur lors de la connexion')
          );
        } else {
          console.error('Erreur réseau:', error);
          setError('Erreur de connexion au serveur. Veuillez réessayer.');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors">
      {/* Header */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-bold text-gray-900 dark:text-white">Unity Learning</span>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 py-16">
        <div className="max-w-4xl w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* Left side - Information */}
            <div className="space-y-8">
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                  Apprenez Unity et C# de manière interactive
                </h1>
                <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                  Plateforme d'apprentissage conçue pour les étudiants de Simplon.co Versailles.
                  Exercices personnalisés, feedback IA, et progression en temps réel.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
                  <Users className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">17</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Étudiants</div>
                </div>
                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
                  <Trophy className="w-8 h-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">10+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Exercices</div>
                </div>
                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
                  <Clock className="w-8 h-8 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">3-7</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Min/exercice</div>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
                  Fonctionnalités incluses
                </h3>
                <ul className="space-y-2 text-blue-800 dark:text-blue-200">
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                    <span>Exercices générés par IA (Gemini)</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                    <span>Feedback détaillé et conseils personnalisés</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                    <span>Suivi de progression en temps réel</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                    <span>Préparation certification Unity Certified User</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right side - Login form */}
            <div className="lg:pl-8">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 lg:p-10">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {isRegisterMode ? 'Créer un compte' : 'Se connecter'}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300">
                    {isRegisterMode
                      ? 'Créez votre compte pour accéder à la plateforme'
                      : 'Connectez-vous avec votre email et mot de passe'
                    }
                  </p>
                </div>

                {notice && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                    <p className="text-yellow-800 dark:text-yellow-200 text-sm">{notice}</p>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                    <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Adresse email
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre.email@exemple.com"
                      className="w-full px-4 py-4 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Mot de passe
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Votre mot de passe"
                      className="w-full px-4 py-4 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      required
                      disabled={isLoading}
                    />
                    {isRegisterMode && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        Minimum 8 caractères avec majuscules, minuscules et chiffres
                      </p>
                    )}
                  </div>

                  {isRegisterMode && (
                    <div>
                      <label htmlFor="pseudonym" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Pseudonyme
                      </label>
                      <input
                        id="pseudonym"
                        type="text"
                        value={pseudonym}
                        onChange={(e) => setPseudonym(e.target.value)}
                        placeholder="Exemple: Alex_Dev, MarieC, JulienScript..."
                        className="w-full px-4 py-4 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        required
                        disabled={isLoading}
                      />
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        Nom d'affichage sur la plateforme (optionnel)
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || !email.trim() || !password || (isRegisterMode && !pseudonym.trim())}
                    className="w-full bg-blue-600 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{isRegisterMode ? 'Création...' : 'Connexion...'}</span>
                      </>
                    ) : (
                      <>
                        <span>{isRegisterMode ? 'Créer mon compte' : 'Se connecter'}</span>
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  {!isRegisterMode && (
                    <div className="text-center">
                      <a
                        href="/request-password"
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                      >
                        Mot de passe oublié ?
                      </a>
                    </div>
                  )}
                </form>

                {/* Toggle between login and register */}
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setIsRegisterMode(!isRegisterMode);
                      setError(''); // Clear any previous errors
                    }}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  >
                    {isRegisterMode ? (
                      <>Vous avez déjà un compte ? <span className="text-blue-600 dark:text-blue-400 font-medium">Se connecter</span></>
                    ) : (
                      <>Pas encore de compte ? <span className="text-blue-600 dark:text-blue-400 font-medium">Créer un compte</span></>
                    )}
                  </button>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
                    En utilisant cette plateforme, vous acceptez de participer au programme d'apprentissage Unity/C#.
                    <br />
                    <strong>Simplon.co Versailles - Formation Unity Certified User Programmer</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;